import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Bot, Upload, X, Copy, MessageSquareDot, ThumbsUp, AlertTriangle, Lightbulb, Sparkles } from "lucide-react";
import { toast } from "sonner";

function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = () => rej(r.error);
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}

export default function AIAssistantPage() {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const fileRef = useRef(null);

  async function onFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Maks 5MB");
    if (!/(image\/(png|jpe?g|webp))/i.test(f.type)) return toast.error("Hanya PNG, JPG, atau WEBP");
    const b64 = await fileToB64(f);
    setImage(b64);
    setAnalysis(null);
  }

  async function analyze() {
    if (!image) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai-assistant/analyze-chat", { image_base64: image });
      setAnalysis(data.analysis || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  function copyReply() {
    if (!analysis?.rekomendasi_balasan) return;
    navigator.clipboard.writeText(analysis.rekomendasi_balasan);
    toast.success("Balasan disalin ke clipboard");
  }

  function reset() {
    if (fileRef.current) fileRef.current.value = "";
    setImage(null);
    setAnalysis(null);
  }

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      <PageHeader overline="AI Assistant" title="Analisa Chat Asli" description="Unggah screenshot chat Anda dengan klien — AI akan membaca isinya, mengevaluasi cara komunikasi Anda, dan memberikan draft balasan terbaik untuk meningkatkan peluang closing." icon={Bot} tone="brand" />

      <Card className="p-6 space-y-4 border-border">
        <p className="overline">1. Unggah Screenshot Chat</p>
        <p className="text-xs text-ink-500">Format: PNG / JPG / WEBP, maks 5MB. AI akan membaca teks chat secara otomatis.</p>

        {!image ? (
          <div className="rounded-lg border-2 border-dashed border-border p-10 text-center">
            <Bot className="mx-auto h-10 w-10 text-ink-300" />
            <p className="mt-2 text-sm text-ink-500">Belum ada gambar.</p>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="input-chat-image" />
            <Button onClick={()=>fileRef.current?.click()} className="mt-3" data-testid="btn-pick-chat-image"><Upload className="mr-2 h-4 w-4" /> Pilih Gambar</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-lg border border-border bg-secondary/20">
              <img src={image} alt="chat" className="max-h-96 w-full object-contain" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={analyze} disabled={loading} data-testid="btn-analyze-chat"><Sparkles className="mr-2 h-4 w-4" /> {loading ? "Menganalisis…" : "Analisa & Rekomendasi"}</Button>
              <Button variant="outline" onClick={reset} disabled={loading}><X className="mr-2 h-4 w-4" /> Ganti Gambar</Button>
            </div>
          </div>
        )}
      </Card>

      {analysis && (
        <div className="space-y-4" data-testid="analysis-result">
          {analysis.raw ? (
            <Card className="p-4 border-border">
              <pre className="whitespace-pre-wrap text-xs">{analysis.raw}</pre>
            </Card>
          ) : (
            <>
              {analysis.ringkasan_percakapan && (
                <Card className="p-5 border-border">
                  <p className="overline mb-1 flex items-center gap-1.5"><MessageSquareDot className="h-3 w-3" /> Ringkasan</p>
                  <p className="text-sm text-ink-700">{analysis.ringkasan_percakapan}</p>
                </Card>
              )}

              {analysis.evaluasi && (
                <Card className="p-5 border-border space-y-4">
                  <div className="flex items-center gap-4">
                    <ScoreBadge score={analysis.evaluasi.skor} />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-500">Skor Komunikasi</div>
                      <div className="font-heading text-lg font-bold text-ink-900">{interpretScore(analysis.evaluasi.skor)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Section icon={ThumbsUp} title="Yang Sudah Baik" items={analysis.evaluasi.yang_sudah_baik} color="border-emerald-200 bg-emerald-50/40" />
                    <Section icon={AlertTriangle} title="Yang Perlu Diperbaiki" items={analysis.evaluasi.yang_perlu_diperbaiki} color="border-rose-200 bg-rose-50/40" />
                  </div>
                </Card>
              )}

              {analysis.rekomendasi_balasan && (
                <Card className="p-5 border-2 border-brand/30 bg-brand/5">
                  <div className="flex items-center justify-between">
                    <p className="overline flex items-center gap-1.5 text-brand"><Sparkles className="h-3 w-3" /> Rekomendasi Balasan</p>
                    <Button size="sm" variant="outline" onClick={copyReply} data-testid="btn-copy-reply"><Copy className="mr-1 h-3 w-3" /> Salin</Button>
                  </div>
                  <div className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-card p-4 text-sm leading-relaxed text-ink-900">{analysis.rekomendasi_balasan}</div>
                </Card>
              )}

              {analysis.tips_tambahan?.length > 0 && (
                <Section icon={Lightbulb} title="Tips Tambahan" items={analysis.tips_tambahan} color="border-amber-200 bg-amber-50/40" />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const tone = s >= 80 ? "from-emerald-500 to-emerald-700" : s >= 60 ? "from-sky-500 to-sky-700" : s >= 40 ? "from-amber-500 to-amber-600" : "from-rose-500 to-rose-700";
  return (
    <div className={`grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br ${tone} text-white shadow-md`}>
      <div className="text-center">
        <div className="font-heading text-2xl font-bold leading-none">{s}</div>
        <div className="text-[9px] uppercase tracking-widest opacity-90">/ 100</div>
      </div>
    </div>
  );
}

function interpretScore(s) {
  const n = Number(s) || 0;
  if (n >= 80) return "Sangat Baik";
  if (n >= 60) return "Baik";
  if (n >= 40) return "Perlu Ditingkatkan";
  return "Perlu Perbaikan Serius";
}

function Section({ icon: Icon, title, items, color }) {
  if (!items?.length) return null;
  return (
    <Card className={`p-4 border ${color || "border-border"}`}>
      <p className="overline mb-2 flex items-center gap-1.5"><Icon className="h-3 w-3" /> {title}</p>
      <ul className="space-y-1.5 text-sm">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-ink-500">•</span><span className="text-ink-900">{it}</span></li>)}
      </ul>
    </Card>
  );
}
